/* src/AuthGuard.js */

// --- React Imports ---
import React, { useEffect, useState } from 'react';
import { Navigate, useLocation, Outlet } from 'react-router-dom';

// --- Context Imports ---
import { useProfile } from './contexts/ProfileContext';
import { useLoading as useLoadingContextHook } from './contexts/LoadingContext';

// --- UI Imports ---
import LoadingScreen from './components/ui/LoadingScreen'; // Or your loading component

const AuthGuard = () => {
    const location = useLocation();
    const {
        authUser,
        isAuthLoading,
        userSubscriptionStatus,
        userSubscriptionEndDate, // Need this to check if a 'canceled' subscription is still valid
        isLoading: isProfileContextLoading, // General loading from ProfileContext (includes sub loading)
        activeProfileId, // Need to check if a profile is selected for most routes
    } = useProfile();
    const { showLoading, hideLoading } = useLoadingContextHook();

    // Combined loading state based on ProfileContext
    const isLoading = isAuthLoading || isProfileContextLoading;

    useEffect(() => {
        if (isLoading) {
            showLoading("Verifying access...");
        } else {
            hideLoading();
        }
        return () => hideLoading();
    }, [isLoading, showLoading, hideLoading]);


    // --- Loading State ---
    if (isLoading) {
        console.log(`[AuthGuard] Loading... Auth:${isAuthLoading}, ProfileCtx:${isProfileContextLoading}. Path: ${location.pathname}`);
        return <LoadingScreen message="Verifying access..." />;
    }

    // --- Authentication Check ---
    // This should ideally be handled by LoggedInGuard for routes *before* AuthGuard,
    // but as a safeguard, it's here too.
    if (!authUser) {
        console.log(`[AuthGuard] No user authenticated (should have been caught by LoggedInGuard if applicable). Redirecting to login from ${location.pathname}`);
        return <Navigate to="/login" state={{ from: location }} replace />;
    }

    // --- Paths that require login but DO NOT require an active subscription OR an active profile ---
    // These are typically account management, settings, or initial profile setup.
    const authOnlyPaths = [
        '/account',
        '/settings',
        '/profile-selection', // User might land here if they have no profiles yet
        '/choose-plan', // User might be sent here from /account if their sub is inactive
        // Note: '/resubscribe' is handled by LoggedInGuard, not AuthGuard, as it's a distinct flow
    ];

    if (authOnlyPaths.includes(location.pathname)) {
        console.log(`[AuthGuard] Path ${location.pathname} requires authentication only. Access granted.`);
        return <Outlet />;
    }

    // --- Active Profile Check (for all routes beyond authOnlyPaths) ---
    // Most of the app requires an active profile to function.
    if (!activeProfileId) {
        console.log(`[AuthGuard] No active profile selected. Redirecting to profile-selection from ${location.pathname}`);
        return <Navigate to="/profile-selection" state={{ from: location, message: "Please select or create a profile to continue." }} replace />;
    }

    // --- Subscription Check (for routes needing an active subscription) ---
    // At this point, user is logged in AND has an active profile.
    // Now, check if their subscription is valid for the current route.

    const isActive = userSubscriptionStatus === 'active';
    const isTrialing = userSubscriptionStatus === 'trialing';
    const isCanceledWithAccess = userSubscriptionStatus === 'canceled' &&
                               userSubscriptionEndDate &&
                               new Date(userSubscriptionEndDate) >= new Date(); // Check if access period is still valid

    const hasValidSubscription = isActive || isTrialing || isCanceledWithAccess;

    if (!hasValidSubscription) {
        console.log(`[AuthGuard] Subscription not active for ${location.pathname}. Status: ${userSubscriptionStatus}. EndDate: ${userSubscriptionEndDate}. Redirecting to /resubscribe.`);
        // Redirect to the new resubscribe screen.
        // Pass a message if needed, though ResubscribeScreen itself explains.
        return <Navigate to="/resubscribe" state={{ from: location }} replace />;
    }

    // --- Access Granted to subscription-protected route ---
    console.log(`[AuthGuard] Access Granted to ${location.pathname}. Auth OK, Profile OK, Subscription: ${userSubscriptionStatus}.`);
    return <Outlet />;
};

export default AuthGuard;